import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import {
  DescriptionCaptionText,
  DescriptionCol,
  DescriptionRow,
} from '@/components/Description';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { useTranslation } from '@/i18n';
import { maxLengthWithSurrogate } from '@/libs/validation/maxLengthWithSurrogate';
import {
  Caption,
  FormInput,
  Label,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styles from './TooltipInput.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  mode: TransactionItemDrawerMode;
};

const MAX_LENGTH = 100;

export const TooltipInput: FC<Props> = memo(({ mode }) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<TransactionItemFormType>();

  const hasError = !!Object.values(errors || {}).length;

  const rules = useMemo(() => {
    return {
      require: false,
      ...maxLengthWithSurrogate(
        MAX_LENGTH,
        t('transaction_items.validation_message.maxLength', {
          maxLength: MAX_LENGTH,
        })
      ),
    };
  }, [t]);

  return (
    <DescriptionRow>
      <DescriptionCol
        label={
          <Label testId='tooltip'>
            <BoldLabelText>
              {t('transaction_items.form.tooltip_message')}
              {mode !== TransactionItemDrawerModeEnum.DETAIL && (
                <Caption type='helper' icon={false}>
                  {t('transaction_items.form.tooltip_message.note')}
                </Caption>
              )}
            </BoldLabelText>
          </Label>
        }
        labelWidth='176px'
      />
      <DescriptionCol
        className={cx(styles['column-wrapper'])}
        value={
          <>
            {mode === TransactionItemDrawerModeEnum.DETAIL ? (
              <div
                className={cx(styles['value-wrapper'])}
                data-testid='tooltip-detail'
              >
                {getValues('tooltip_message') ?? ''}
              </div>
            ) : (
              <div className={cx(styles['input-wrapper'])}>
                <FormInput<TransactionItemFormType>
                  name='tooltip_message'
                  control={control}
                  inputId='tooltip_message'
                  testId='tooltip_message'
                  rules={rules}
                />
                {!hasError && (
                  <DescriptionCaptionText>
                    {t('transaction_items.form.tooltip_message.caption', {
                      maxLength: MAX_LENGTH,
                    })}
                  </DescriptionCaptionText>
                )}
              </div>
            )}
          </>
        }
      />
    </DescriptionRow>
  );
});
TooltipInput.displayName = 'TooltipInput';
